import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  AutoAwesome,
  Add,
  Edit,
  Delete,
  Visibility,
  Assessment,
  QuestionAnswer,
} from '@mui/icons-material';
import { AIQuestionGenerator, GenerationConfig } from './AIQuestionGenerator';
import { ManualQuestionDialog } from './ManualQuestionDialog';
import {
  createPracticeQuestion,
  generatePracticeQuestions,
  getPracticeQuestions,
  deletePracticeQuestionSet,
  getStudentPracticeProgress,
  updatePracticeQuestion,
} from '../../services/lessonService';
import {
  CreatePracticeQuestionRequest,
  PracticeQuestion,
  PracticeQuestionSet,
} from '../../types/lesson';
import { RichTextEditor } from '../shared/RichTextEditor';

interface PracticeQuestionManagerProps {
  moduleId: string;
  unitId: string;
  unitTitle: string;
}

export const PracticeQuestionManager: React.FC<PracticeQuestionManagerProps> = ({
  moduleId,
  unitId,
  unitTitle,
}) => {
  const [practiceSet, setPracticeSet] = useState<PracticeQuestionSet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<PracticeQuestion | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [studentsProgress, setStudentsProgress] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generationMetadata, setGenerationMetadata] = useState<any>(null);

  useEffect(() => {
    loadPracticeQuestions();
  }, [moduleId, unitId]);

  const loadPracticeQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPracticeQuestions(moduleId, unitId);
      setPracticeSet(response.practice_questions);
    } catch (err: any) {
      console.error('Error loading practice questions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (config: GenerationConfig) => {
    try {
      const result = await generatePracticeQuestions(moduleId, unitId, config);
      setPracticeSet(result.practice_set);
      setGenerationMetadata(result.generation_metadata);
      setGeneratorOpen(false);

      // Show success message
      alert(`Successfully generated ${result.practice_set.questions.length} practice questions!`);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to generate practice questions');
    }
  };

  const handleCreateManualQuestion = async (
    question: CreatePracticeQuestionRequest['question'],
  ) => {
    const result = await createPracticeQuestion(moduleId, unitId, {
      question,
      set_title: `Practice Questions - ${unitTitle}`,
      set_description: `Practice questions for ${unitTitle}`,
    });
    setPracticeSet(result.practice_set);
    setGenerationMetadata(null);
  };

  const handleEditQuestion = (question: PracticeQuestion) => {
    setSelectedQuestion(question);
    setEditDialogOpen(true);
  };

  const handleUpdateQuestion = async (question: CreatePracticeQuestionRequest['question']) => {
    if (!practiceSet || !selectedQuestion) return;

    const result = await updatePracticeQuestion(
      moduleId,
      unitId,
      practiceSet.set_id,
      selectedQuestion.question_id,
      question,
    );
    setPracticeSet({
      ...practiceSet,
      questions: practiceSet.questions.map((item) =>
        item.question_id === selectedQuestion.question_id ? result.question : item,
      ),
      updated_at: new Date().toISOString(),
    });
    setSelectedQuestion(null);
  };

  const handleOpenProgress = async () => {
    try {
      setProgressDialogOpen(true);
      setProgressLoading(true);
      setProgressError(null);
      const result = await getStudentPracticeProgress(moduleId, unitId);
      setStudentsProgress(result.students_progress || []);
    } catch (err: any) {
      setProgressError(err.message || 'Failed to load practice progress');
    } finally {
      setProgressLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!practiceSet) return;

    try {
      setDeleting(true);
      await deletePracticeQuestionSet(moduleId, unitId, practiceSet.set_id);
      setPracticeSet(null);
      setGenerationMetadata(null);
      setDeleteDialogOpen(false);
      alert('Practice question set deleted successfully');
    } catch (err: any) {
      alert(err.message || 'Failed to delete practice question set');
    } finally {
      setDeleting(false);
    }
  };

  const questionSummary = (questionText: string) => {
    const plainText = questionText
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const summary = plainText || questionText;
    return `${summary.substring(0, 100)}${summary.length > 100 ? '...' : ''}`;
  };

  const progressSummary = (entry: any) => {
    const progressItems = Array.isArray(entry.progress) ? entry.progress : [];
    return (
      progressItems.find((item: any) => item.set_id === practiceSet?.set_id) ||
      progressItems[0] ||
      null
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          <QuestionAnswer sx={{ mr: 1, verticalAlign: 'middle' }} />
          Practice Questions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create practice questions for students to reinforce their learning
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* No Practice Questions Yet */}
      {!practiceSet && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <AutoAwesome sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Practice Questions Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate practice questions with AI to help students reinforce their learning. Perfect
              for math, science, and other subjects requiring practice.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesome />}
              onClick={() => setGeneratorOpen(true)}
            >
              Generate Questions with AI
            </Button>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => setManualDialogOpen(true)}
              >
                Add Question Manually
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Existing Practice Questions */}
      {practiceSet && (
        <Box>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6">{practiceSet.title}</Typography>
                  {practiceSet.description && (
                    <Typography variant="body2" color="text.secondary">
                      {practiceSet.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPreviewDialogOpen(true)}
                    disabled={practiceSet.questions.length === 0}
                    title="Edit questions"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setPreviewDialogOpen(true)}
                    title="Preview"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleOpenProgress}
                    title="View student progress"
                  >
                    <Assessment />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    title="Delete"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Question Summary */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={`${practiceSet.questions.length} Questions`}
                  color="primary"
                  size="small"
                />
                {generationMetadata && (
                  <>
                    <Chip
                      label={`AI Generated (${generationMetadata.model})`}
                      size="small"
                      icon={<AutoAwesome />}
                    />
                    <Chip
                      label={`Cost: $${generationMetadata.cost.toFixed(4)}`}
                      size="small"
                      variant="outlined"
                    />
                  </>
                )}
              </Box>

              {/* Question List */}
              <Typography variant="subtitle2" gutterBottom>
                Questions:
              </Typography>
              <List dense>
                {practiceSet.questions.slice(0, 5).map((question, index) => (
                  <ListItem
                    key={question.question_id}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleEditQuestion(question)}
                        title="Edit question"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={`${index + 1}. ${questionSummary(question.question_text)}`}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={question.question_type.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={question.difficulty}
                            size="small"
                            color={
                              question.difficulty === 'easy'
                                ? 'success'
                                : question.difficulty === 'medium'
                                ? 'warning'
                                : 'error'
                            }
                          />
                          {question.ai_generated && (
                            <Chip label="AI" size="small" icon={<AutoAwesome />} />
                          )}
                          {!question.ai_generated && <Chip label="Manual" size="small" />}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                {practiceSet.questions.length > 5 && (
                  <ListItem>
                    <ListItemText
                      secondary={`... and ${practiceSet.questions.length - 5} more questions`}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>

            <CardActions>
              <Button startIcon={<AutoAwesome />} onClick={() => setGeneratorOpen(true)}>
                Regenerate with AI
              </Button>
              <Button startIcon={<Add />} onClick={() => setManualDialogOpen(true)}>
                Add Question
              </Button>
              <Button
                startIcon={<Edit />}
                onClick={() => setPreviewDialogOpen(true)}
                disabled={practiceSet.questions.length === 0}
              >
                Edit Questions
              </Button>
            </CardActions>
          </Card>

          {/* Metadata Card */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Practice Set Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(practiceSet.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Updated: {new Date(practiceSet.updated_at).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created by: {practiceSet.created_by}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* AI Question Generator Dialog */}
      <AIQuestionGenerator
        open={generatorOpen}
        onClose={() => setGeneratorOpen(false)}
        onGenerate={handleGenerate}
        unitTitle={unitTitle}
      />

      <ManualQuestionDialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        onSave={handleCreateManualQuestion}
        unitTitle={unitTitle}
      />

      <ManualQuestionDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedQuestion(null);
        }}
        onSave={handleUpdateQuestion}
        unitTitle={unitTitle}
        initialQuestion={selectedQuestion}
        title="Edit Practice Question"
      />

      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Preview Practice Questions</DialogTitle>
        <DialogContent dividers>
          {practiceSet && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {practiceSet.questions.map((question, index) => (
                <Card key={question.question_id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip label={`Question ${index + 1}`} size="small" color="primary" />
                      <Chip label={question.question_type.replace('_', ' ')} size="small" />
                      <Chip label={question.difficulty} size="small" variant="outlined" />
                      <Chip label={`${question.points ?? 1} pts`} size="small" color="secondary" />
                    </Box>
                    <RichTextEditor
                      content={question.question_text}
                      onChange={() => undefined}
                      readOnly
                      minHeight={0}
                    />
                    {question.options && question.options.length > 0 && (
                      <List dense>
                        {question.options.map((option) => (
                          <ListItem key={option.option_id}>
                            <ListItemText
                              primary={`${option.option_id}. ${option.text}`}
                              secondary={option.is_correct ? 'Correct answer' : undefined}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                    {question.correct_answer !== undefined && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Answer:</strong> {String(question.correct_answer)}
                      </Typography>
                    )}
                    {question.explanation && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Explanation:</strong> {question.explanation}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleEditQuestion(question)}
                    >
                      Edit
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Practice Progress</DialogTitle>
        <DialogContent dividers>
          {progressLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : progressError ? (
            <Alert severity="error">{progressError}</Alert>
          ) : studentsProgress.length === 0 ? (
            <Alert severity="info">No student progress has been recorded yet.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {studentsProgress.map((entry) => {
                const summary = progressSummary(entry);
                const attempted = summary?.questions_attempted?.length || 0;
                const correct = summary?.questions_correct?.length || 0;
                const accuracy = summary?.accuracy_rate || 0;
                return (
                  <Card key={entry.student_id} variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Student {entry.student_id}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {correct} correct / {attempted} attempted
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {accuracy}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, accuracy))}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Practice Question Set?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this practice question set? This action cannot be
            undone. All student progress on these questions will be preserved but the questions will
            no longer be available.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
